import { LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form } from 'antd'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useStocksServiceCreateWriteOffEndpoint,
  useStocksServiceGetWriteOffEndpoint,
  useStocksServiceGetWriteOffListEndpointKey,
  useStocksServiceSendWriteOffEndpoint,
  useStocksServiceUpdateWriteOffEndpoint
} from 'src/api/queries'
import { ApiError, InventoryDocumentState, Permissions } from 'src/api/requests'
import {
  FormFooter,
  LoadingScreen,
  message,
  PageBreadcrumbs,
  SaveConfirm
} from 'src/components'
import { useFormStyles } from 'src/features/stock/styles'
import {
  useFileHandler,
  useScrollToFirstError,
  useUser,
  ValidationError
} from 'src/hooks'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, toDayjs } from 'src/utils'

import { CreateForm } from './CreateForm'

type MedicineProps = {
  inventoryItemId: string
  quantity: number
  unitPrice: number
}

type ChangeValuesProps = {
  medicine: MedicineProps[]
}

export const StockSettlementsCreatePage = () => {
  const [form] = Form.useForm()
  const { id } = useParams()
  const queryClient = useQueryClient()

  const { userData, hasPermission } = useUser()
  const navigate = useNavigate()
  const { t, currentLang } = useLocales()

  const { styles } = useFormStyles()
  const { onScrollToError } = useScrollToFirstError(form)

  const docs = Form.useWatch('docs', form)

  const {
    existingFiles,
    setExistingFiles,
    onFileChange,
    saveFiles,
    isUploading
  } = useFileHandler({
    form,
    folder: id ? `write-offs-${id}` : `write-offs`
  })

  const onMutationSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [useStocksServiceGetWriteOffListEndpointKey]
    })
    onBackClick()
  }

  const { mutate, isPending } = useStocksServiceCreateWriteOffEndpoint({
    onSuccess: async () => {
      message.success(t('stock_write_offs.write_off_added_success'))
      onMutationSuccess()
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { mutateAsync: update } = useStocksServiceUpdateWriteOffEndpoint({
    onSuccess: () => {
      message.success(t('common.save_success'))
      onMutationSuccess()
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { mutateAsync: send } = useStocksServiceSendWriteOffEndpoint({
    onSuccess: () => {
      message.success(t('stock_applications.settlement_sent'))
      onMutationSuccess()
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { data, isLoading } = useStocksServiceGetWriteOffEndpoint(
    { id: String(id) },
    [String(id)],
    { enabled: !!id }
  )

  const onBackClick = () => {
    form.resetFields()
    navigate(paths.settlements.root)
  }

  const onSave = async (isSend: boolean) => {
    try {
      const values = await form.validateFields()

      const items = values?.medicine?.map((item: MedicineProps) => ({
        inventoryItemId: item?.inventoryItemId,
        quantity: item?.quantity
      }))

      const shouldSaveFiles = docs?.length !== existingFiles.length

      let mediaItems = existingFiles

      if (shouldSaveFiles) {
        const uploadedFiles = await saveFiles()
        mediaItems = uploadedFiles
      }

      const payload = {
        institutionId: String(userData?.currentLegalEntityId),
        documentNumber: String(values?.documentNumber),
        documentDate: toDayjs(values?.invoiceDate)?.format(yearMonthDaySlash),
        comment: values?.comment,
        reasons: values?.reasons.map(
          (reason: { value: string }) => reason.value
        ),
        items,
        mediaItems
      }

      if (id) {
        await update({
          id: String(id),
          requestBody: {
            comment: values.comment,
            items,
            mediaItems,
            reasons: values?.reasons.map(
              (reason: { value: string }) => reason.value
            )
          }
        })

        if (isSend) {
          await send({
            id: String(id),
            requestBody: { comment: values?.comment }
          })
        }
      } else {
        mutate({ requestBody: payload })
      }
    } catch (error) {
      onScrollToError(error as ValidationError)
    }
  }

  useEffect(() => {
    if (id && data) {
      const existingFileIds = data?.mediaFiles
        ?.map((file) => file?.id)
        .filter(Boolean) as string[]

      setExistingFiles(existingFileIds)

      form.setFieldsValue({
        documentDate: toDayjs(data?.documentDate),
        documentNumber: data?.documentNumber,
        comment: data?.comment,
        reasons: data?.reasons?.map((reason) => ({
          label: reason?.translation?.[currentLang?.value],
          value: reason?.id
        })),
        medicine: data?.items?.map((item) => ({
          barCode: item?.barCode,
          inventoryItemId: item.inventoryItemId,
          name: `${item?.internationalName?.name} ${item?.doses}`,
          productName: item?.product?.name,
          producerName: item?.producer?.name,
          expirationDate: toDayjs(item?.expirationDate)?.format(
            yearMonthDaySlash
          ),
          unitPrice: item?.unitPrice,
          priceTotal: (item?.quantity ?? 0) * (item?.unitPrice ?? 0),
          quantity: `${item?.quantity}`
        })),
        docs: existingFileIds?.map((id) => ({
          uid: id,
          name: `File-${id}`,
          status: 'done'
        }))
      })
    }
  }, [data, id])

  const updateTotalPrice = () => {
    const medicines = form?.getFieldValue('medicine') || []

    const updatedMedicines = medicines.map((med: MedicineProps) => {
      return {
        ...med,
        priceTotal:
          med?.quantity && med?.unitPrice
            ? Math.round(med.quantity * med.unitPrice * 100) / 100
            : undefined
      }
    })
    form?.setFieldsValue({ medicine: updatedMedicines })
  }

  const onValuesChange = (changedValues: ChangeValuesProps) => {
    if (changedValues.medicine) {
      updateTotalPrice()
    }
  }

  const pageTitle = t(
    `stock_write_offs.${id ? 'edit_write_off' : 'add_new_write_off'}`
  )

  const canSend = hasPermission(Permissions.STOCKS_STOCK_SEND_WRITE_OFFS)
  const canUpdate = hasPermission(Permissions.STOCKS_STOCK_UPDATE_WRITE_OFFS)

  const isNew = data?.state === InventoryDocumentState.NEW
  const isRejected = data?.state === InventoryDocumentState.REJECTED
  const isEditableDraft = data && (isNew || isRejected)
  const isCreate = !data
  const showFormFooter = !!(canUpdate && isEditableDraft)

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      title={pageTitle}
      items={[
        {
          title: t('menu.consumable_medicaments'),
          path: paths.settlements.root
        },
        {
          title: t('stock.settlement'),
          path: paths.settlements.root
        },
        {
          title: pageTitle
        }
      ]}
      extra={
        <Flex gap={8}>
          {!showFormFooter && (
            <Button onClick={onBackClick}>{t('common.cancel')}</Button>
          )}

          {isCreate && (
            <SaveConfirm
              title={`${t('stock_applications.save_as_draft')}?`}
              placement="leftBottom"
              onConfirm={() => onSave(false)}
              okButtonProps={{ loading: isUploading || isLoading || isPending }}
            >
              <Button>{t('stock_applications.save_as_draft')}</Button>
            </SaveConfirm>
          )}
          {canSend && isNew && (
            <SaveConfirm
              placement="leftBottom"
              description={t('stock_applications.confirm_send_desc')}
              title={t('stock_applications.confirm_send_title')}
              onConfirm={() => onSave(true)}
              okButtonProps={{ loading: isUploading || isLoading || isPending }}
            >
              <Button type="primary">{t('stock_applications.send')}</Button>
            </SaveConfirm>
          )}
        </Flex>
      }
    >
      <Flex vertical gap={24} className={styles.form}>
        <Button
          icon={<LeftOutlined />}
          className="fit-content"
          onClick={onBackClick}
        >
          {t('common.back')}
        </Button>

        <Form
          layout="vertical"
          form={form}
          requiredMark={false}
          onValuesChange={onValuesChange}
          scrollToFirstError
        >
          <CreateForm form={form} state={data?.state} onChange={onFileChange} />

          <FormFooter
            hidden={!showFormFooter}
            onCancel={onBackClick}
            onSave={() => onSave(false)}
            saveLabel={t('common.update')}
            saveConfirmTitle={`${t('common.update')}?`}
            loading={isUploading || isLoading || isPending}
          />
        </Form>
      </Flex>
    </PageBreadcrumbs>
  )
}
